import { ORDER_PARENT_BRANDS } from "../constants/orderParentBrands";
import { UNIQUE_BRAND_NAMES } from "constants/uniqueBrandNames";
import { convertToTitleCase } from "./string";
import { NoDesignerText, UnavailableDataPlaceholderText } from "../constants/text.ts";

import { models } from "types/api/viewModels.ts";
import { OrderType } from "types/declarations/enums.ts";

export const filterSpecificBrands = (parentBrand: string | undefined | null) => {
	const uniqueBrand = UNIQUE_BRAND_NAMES.find((i) => i.input === parentBrand);
	if (uniqueBrand) {
		return uniqueBrand.output;
	} else {
		return convertToTitleCase(parentBrand);
	}
};

export const getBrandDisplayName = (brand: string | undefined | null) => {
	const normalizedBrand = brand?.trim().replaceAll("_", " ");
	if (!normalizedBrand) {
		return "";
	}

	const needsNormalization =
		normalizedBrand === normalizedBrand.toUpperCase() ||
		normalizedBrand === normalizedBrand.toLowerCase() ||
		brand?.includes("_") === true;

	return needsNormalization ? filterSpecificBrands(normalizedBrand.toUpperCase()) : normalizedBrand;
};

export const brandCheck = (parentBrand: string | undefined | null) => {
	return !!(
		parentBrand !== "UNKNOWN" &&
		ORDER_PARENT_BRANDS.find((brand: string | undefined | null) => brand === parentBrand)
	);
};

export const displayDraftDesigner = (
	designerName: string | undefined | null,
	designerNumber: string | undefined | null,
	isFilter?: boolean
) => {
	if (
		(designerName && designerName !== UnavailableDataPlaceholderText) ||
		(designerNumber && designerNumber !== UnavailableDataPlaceholderText)
	) {
		return [designerName, designerNumber].filter(Boolean).join(" - ");
	} else {
		return isFilter ? NoDesignerText : UnavailableDataPlaceholderText;
	}
};

export const displayDesigner = (designer: models["DesignerViewModel"] | string | undefined | null) => {
	if (typeof designer === "string") {
		return designer;
	}

	const designerName = [designer?.firstName, designer?.lastName].filter(Boolean).join(" ");
	const designerDisplay = [designerName, designer?.designerNumber].filter(Boolean).join(" - ");

	return designerDisplay || UnavailableDataPlaceholderText;
};

export const getOrderTypeDisplayLabel = (orderType: string | undefined | null) => {
	if (!orderType) {
		return "";
	}

	if (orderType === "ADD_ON") {
		return "Add-On";
	} else if (orderType === "QUOTE_ORDER") {
		return "Quote";
	} else if (orderType == "MERCH") {
		return "Marketing Materials";
	}
	return convertToTitleCase(orderType);
};

export const isCampaignOrder = (orderType: string | undefined | null) => orderType === OrderType.CAMPAIGN;

type OrderWithConfigurations = {
	configurations?: Array<{ lineItems?: unknown[] | null } | null | undefined> | null;
} | null;

export const hasOrderLineItems = (
	...orders: Array<OrderWithConfigurations[] | OrderWithConfigurations | undefined>
) => {
	const allConfigurations = orders.flatMap((order) => {
		if (Array.isArray(order)) {
			return order.flatMap((entry) => entry?.configurations ?? []);
		}
		return order?.configurations ?? [];
	});

	return allConfigurations.some((configuration) => (configuration?.lineItems?.length ?? 0) > 0);
};

export const isMerchOrCampaignOrder = (orderType: string | undefined | null) =>
	orderType === OrderType.MERCH || isCampaignOrder(orderType);

export const sortBy = (initOrders: any, sortBy: string = "order-date") => {
	const orders = [...initOrders];

	if (orders.length > 1) {
		const alpha = orders.sort((a: any, b: any) => {
			// Since alphabet is second tier, orders gets sorted alphabetically first
			const jobNameA = a.jobName || "";
			const jobNameB = b.jobName || "";
			if (jobNameA < jobNameB) return -1;
			if (jobNameA > jobNameB) return 1;
			const orderNumberA = parseInt(a.orderNumber);
			const orderNumberB = parseInt(b.orderNumber);
			return orderNumberA - orderNumberB;
		});
		if (sortBy === "Order Date") {
			return alpha.sort((a: any, b: any) => {
				const dateA = new Date(a.orderDate).getTime();
				const dateB = new Date(b.orderDate).getTime();
				return dateB - dateA;
			});
		}

		if (sortBy === "Last Updated") {
			return alpha.sort((a: any, b: any) => {
				const dateA = new Date(a.updatedAt).getTime();
				const dateB = new Date(b.updatedAt).getTime();
				return dateB - dateA;
			});
		}
	}
	return orders;
};

export const getCabCubeCountsFromDraft = (draft: models["DraftOrderViewModel"]) => {
	let cabinetCount = 0;
	let cubeCount = 0;

	const allLineItems = draft.configurations?.flatMap((config) => config.lineItems) ?? [];
	for (const item of allLineItems) {
		cabinetCount += item?.cabinetCount ?? 0;
		cubeCount += item?.cubeCount ?? 0;
	}

	return { cabinetCount, cubeCount };
};

export const combinedJobNameAndPO = (draftOrder: models["DraftOrderViewModel"]) =>
	[draftOrder.jobName, draftOrder.poNumber].filter(Boolean).join(" - ");
